import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#F9F8F6] pt-28 md:pt-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-6 md:gap-10 items-end pb-16 md:pb-24">
        {/* Text block */}
        <div className="col-span-12 md:col-span-7 lg:col-span-6 order-2 md:order-1">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            data-testid="hero-overline"
            className="text-[13px] md:text-sm uppercase tracking-[0.32em] text-[#2D3730] font-semibold mb-8 md:mb-12"
          >
            — A Movement To Redefine Fatherhood
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.15 }}
            data-testid="hero-headline"
            className="font-serif-display text-[#2D3730] font-light leading-[1.02] tracking-tight text-[56px] sm:text-[80px] md:text-[96px] lg:text-[120px]"
          >
            Turning good
            <br />
            fathers into
            <br />
            <span className="italic text-[#C06E52]">FAB</span> fathers.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.45 }}
            data-testid="hero-sub"
            className="mt-10 md:mt-12 max-w-[520px] text-[17px] md:text-[19px] leading-relaxed text-[#5C665F] font-light"
          >
            {`Long after childhood is over, children carry the confidence, resilience, and emotional strength they learned at home. FabFathers exists to help fathers build that legacy, one meaningful moment at a time.`}
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.7 }}
            className="mt-12 md:mt-16 flex items-center gap-8"
          >
            <a
              href="#mission"
              data-testid="hero-explore"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("mission")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="group inline-flex items-center gap-3 text-[#2D3730]"
            >
              <span className="text-[12px] uppercase tracking-[0.24em] font-medium">
                Explore the Why
              </span>
              <ArrowDown
                size={16}
                className="transition-transform duration-500 group-hover:translate-y-1"
              />
            </a>
            <div className="hidden sm:block h-px w-24 bg-[#2D3730]/30" />
            <span className="hidden sm:inline-block text-[11px] uppercase tracking-[0.24em] text-[#8A9A86]">
              Est. for the next generation
            </span>
          </motion.div>
        </div>

        {/* Image block */}
        <motion.div
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, ease: "easeOut" }}
          className="col-span-12 md:col-span-5 lg:col-span-6 order-1 md:order-2 relative"
        >
          <div className="relative h-[420px] sm:h-[520px] md:h-[600px] lg:h-[680px] flex items-center justify-center bg-[#F2EFEB]">
            <img
              src="https://customer-assets.emergentagent.com/job_vision-driven/artifacts/abbfv82f_Web%20First%20IMAGE.png"
              alt="A father gently placing his hand on a child's heart, with warm light radiating between them — the quiet power of emotional presence"
              data-testid="hero-image"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="absolute -bottom-4 -left-4 md:-left-10 bg-[#F9F8F6] px-4 py-3 md:px-6 md:py-4 border border-[#2D3730]/10 shadow-sm">
            <p className="font-serif-display italic text-[#2D3730] text-lg md:text-2xl leading-tight max-w-[260px]">
              {`"A man never stands as tall as when he kneels to help a child."`}
            </p>
          </div>
        </motion.div>
      </div>

      {/* Marquee */}
      <div className="border-y border-[#2D3730]/15 bg-[#F2EFEB] overflow-hidden">
        <div className="marquee-track whitespace-nowrap flex gap-16 py-5 text-[13px] uppercase tracking-[0.3em] text-[#2D3730]">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-16">
              <span>Emotional Intelligence</span>
              <span className="text-[#C06E52]">✦</span>
              <span>Presence Over Presents</span>
              <span className="text-[#C06E52]">✦</span>
              <span>Steady Emotional Anchors</span>
              <span className="text-[#C06E52]">✦</span>
              <span>Raising Resilient Children</span>
              <span className="text-[#C06E52]">✦</span>
              <span>{`A Father's Quiet Power`}</span>
              <span className="text-[#C06E52]">✦</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
